import { logger } from "@/lib/logger";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface AutoBillingParams {
  trial_id: string;
  trial_site_id: string;
  patient_id: string;
  event_trigger_type: string;
  visit_id?: string;
  notes?: string;
}

export function useAutoBillingTrigger() {
  const { toast } = useToast();

  const triggerAutoBilling = async ({
    trial_id,
    trial_site_id,
    patient_id,
    event_trigger_type,
    visit_id,
    notes,
  }: AutoBillingParams) => {
    try {
      // Find matching fee schedule for this event
      const { data: feeSchedules, error: feeError } = await supabase
        .from('protocol_fee_schedules')
        .select('*')
        .eq('trial_id', trial_id)
        .eq('event_trigger_type', event_trigger_type)
        .eq('is_active', true);

      if (feeError) throw feeError;

      if (!feeSchedules || feeSchedules.length === 0) {
        logger.debug(`No fee schedule found for event: ${event_trigger_type}`);
        return;
      }

      // Create billable event for each matching fee schedule
      const billableEvents = feeSchedules.map(schedule => ({
        trial_site_id,
        patient_id,
        fee_schedule_id: schedule.id,
        event_date: new Date().toISOString(),
        unit_amount: schedule.amount,
        quantity: 1,
        total_amount: schedule.amount,
        currency: schedule.currency,
        status: 'pending',
        visit_id: visit_id || null,
        notes: notes || `Auto-billed: ${schedule.fee_name}`,
      }));

      const { error: insertError } = await supabase
        .from('billable_events')
        .insert(billableEvents);

      if (insertError) throw insertError;

      toast({
        title: "Billable event created",
        description: `${billableEvents.length} event(s) auto-billed for ${event_trigger_type.replace(/_/g, ' ')}`,
      });

      return billableEvents.length;
    } catch (error: any) {
      logger.error('Auto-billing failed:', error);
      toast({
        title: "Auto-billing failed",
        description: error.message || "Could not create billable event",
        variant: "destructive",
      });
      return 0;
    }
  };

  return { triggerAutoBilling };
}
